import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Pencil, Plus, Trash2, ShieldCheck } from "lucide-react";

export default function AdminRoles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", is_manager: false });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/roles");
      setRoles(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", is_manager: false });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, is_manager: r.is_manager });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("El nombre es obligatorio");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.patch(`/roles/${editing.id}`, form);
        setRoles((p) => p.map((x) => (x.id === data.id ? data : x)));
        toast.success("Rol actualizado");
      } else {
        const { data } = await api.post("/roles", form);
        setRoles((p) => [...p, data]);
        toast.success("Rol creado");
      }
      setOpen(false);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`¿Eliminar el rol "${r.name}"?`)) return;
    try {
      await api.delete(`/roles/${r.id}`);
      setRoles((p) => p.filter((x) => x.id !== r.id));
      toast.success("Rol eliminado");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF7878]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-[#4A3B32]" data-testid="roles-title">Roles</h1>
          <p className="text-[#8A796F]">Crea y edita los roles de tu equipo.</p>
        </div>
        <Button onClick={openNew} data-testid="role-create-button" className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold px-6">
          <Plus className="w-4 h-4 mr-2" /> Nuevo rol
        </Button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.id} className="bg-white border border-[#EADBCE] rounded-2xl p-5" data-testid={`role-row-${r.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-display font-bold text-lg text-[#4A3B32]">{r.name}</h3>
                {r.is_manager && (
                  <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-[#A7E4D3]/40 text-[#4A3B32] font-semibold mt-1">
                    <ShieldCheck className="w-3 h-3" /> Gerente
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`role-edit-${r.id}`} className="rounded-full">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <Button size="sm" variant="ghost" onClick={() => remove(r)} data-testid={`role-delete-${r.id}`} className="rounded-full text-[#FF5C5C] hover:text-[#FF5C5C] hover:bg-[#FFF0F0]">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Borrar
              </Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-[#EADBCE] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Editar rol" : "Nuevo rol"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ej. Servicio, Producción, Caja..."
                className="h-12 rounded-xl border-[#EADBCE]"
                data-testid="role-name-input"
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-[#EADBCE] p-3">
              <div>
                <p className="font-semibold text-sm text-[#4A3B32]">Es rol de Gerente</p>
                <p className="text-xs text-[#8A796F]">Puede administrar usuarios, items y recibir notificaciones.</p>
              </div>
              <Switch
                checked={form.is_manager}
                onCheckedChange={(v) => setForm({ ...form, is_manager: v })}
                data-testid="role-manager-switch"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancelar</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
              data-testid="role-save-button"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Guardar" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
